import {
	ActionButton,
	Button,
	ButtonGroup,
	Content,
	DropZone,
	Heading,
	IllustratedMessage,
	Text,
} from '@react-spectrum/s2';
import CloudUpload from '@react-spectrum/s2/illustrations/gradient/generic1/CloudUpload';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DropEvent } from 'react-aria';

import { SuccessCheckBadge } from '@/components/SuccessCheckBadge';
import {
	CONTACTS_FILE_INPUT_ACCEPT,
	ignoredSheetsNotice,
	MAX_CSV_SIZE,
	parseContactsCsv,
	type ParsedContactRow,
	readContactsFileAsCsv,
} from '@/utils/contactsCsv';

type ImportRow = ParsedContactRow;

interface ImportContactsDialogProps {
	onImport: (rows: ImportRow[]) => Promise<{ savedCount: number }>;
	isDisabled?: boolean;
}

type Status = 'idle' | 'importing' | 'complete';

const FileIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
		<polyline points="14 2 14 8 20 8" />
	</svg>
);

const CloseIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<line x1="18" y1="6" x2="6" y2="18" />
		<line x1="6" y1="6" x2="18" y2="18" />
	</svg>
);

export function ImportContactsDialog({
	onImport,
	isDisabled = false,
}: ImportContactsDialogProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [status, setStatus] = useState<Status>('idle');
	const [rows, setRows] = useState<ImportRow[]>([]);
	const [fileName, setFileName] = useState<string | null>(null);
	const [skippedCount, setSkippedCount] = useState(0);
	const [savedCount, setSavedCount] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const reset = useCallback(() => {
		setStatus('idle');
		setRows([]);
		setFileName(null);
		setSkippedCount(0);
		setSavedCount(0);
		setError(null);
		setNotice(null);
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
		reset();
	}, [reset]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && status !== 'importing') close();
		};
		document.addEventListener('keydown', onKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, status, close]);

	const handleFile = useCallback(async (file: File) => {
		setError(null);
		setNotice(null);
		setStatus('idle');
		setRows([]);
		setSkippedCount(0);
		setSavedCount(0);
		setFileName(file.name);

		if (file.size > MAX_CSV_SIZE) {
			setError('File size exceeds 5MB limit');
			return;
		}

		let text: string;
		let ignoredSheetNames: string[];
		try {
			({ csv: text, ignoredSheetNames } = await readContactsFileAsCsv(file));
		} catch {
			setError('Couldn’t read the file. Please try again.');
			return;
		}

		const parsed = parseContactsCsv(text);
		if (parsed.error) {
			setError(parsed.error);
			return;
		}

		setNotice(ignoredSheetsNotice(ignoredSheetNames));

		const dataRowCount =
			text.split(/\r?\n/).filter((row) => row.trim().length > 0).length - 1;
		setRows(parsed.contacts);
		setSkippedCount(Math.max(0, dataRowCount - parsed.contacts.length));
	}, []);

	const handleDrop = useCallback(
		(e: DropEvent) => {
			const fileItem = e.items.find((item) => item.kind === 'file');
			if (fileItem?.kind === 'file') {
				void fileItem.getFile().then((file) => handleFile(file));
			}
		},
		[handleFile],
	);

	const handleImport = useCallback(async () => {
		if (rows.length === 0) return;
		setStatus('importing');
		setError(null);
		try {
			const result = await onImport(rows);
			setSavedCount(result.savedCount);
			setStatus('complete');
		} catch {
			setError('Couldn’t import contacts. Please try again.');
			setStatus('idle');
		}
	}, [onImport, rows]);

	const hasFile = rows.length > 0;
	const isImporting = status === 'importing';

	return (
		<>
			<Button
				variant="secondary"
				fillStyle="outline"
				isDisabled={isDisabled}
				data-testid="import-csv-trigger"
				onPress={() => setIsOpen(true)}
			>
				Import contacts
			</Button>

			{isOpen && (
				<div
					data-testid="import-contacts-overlay"
					className="fixed inset-0 z-50 flex items-center justify-center
						bg-black/40 p-4 backdrop-blur-sm"
					onClick={close}
				>
					<div
						data-testid="import-contacts-dialog"
						className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl
							border border-(--glass-border) bg-white shadow-2xl"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-6 pt-5 pb-4">
							<h2 className="text-lg font-semibold text-(--text-primary)">
								Import contacts
							</h2>
							<button
								type="button"
								aria-label="Close"
								data-testid="import-contacts-close"
								onClick={close}
								className="flex h-8 w-8 items-center justify-center rounded-full
									text-(--text-muted) transition-colors
									hover:bg-(--glass-bg-hover) hover:text-(--text-primary)"
							>
								<CloseIcon />
							</button>
						</div>

						{status === 'complete' ? (
							<div className="flex flex-col items-center gap-4 px-6 pt-2 pb-8">
								<SuccessCheckBadge />
								<p
									data-testid="import-contacts-complete"
									className="text-base font-medium text-(--text-primary)"
								>
									Imported {savedCount} contact{savedCount === 1 ? '' : 's'}
								</p>
								<Button
									variant="accent"
									data-testid="import-contacts-done"
									onPress={close}
								>
									<Text>Done</Text>
								</Button>
							</div>
						) : (
							<>
								<input
									ref={fileInputRef}
									type="file"
									accept={CONTACTS_FILE_INPUT_ACCEPT}
									className="hidden"
									aria-label="Choose contacts file"
									data-testid="import-contacts-file-input"
									onChange={(e) => {
										const file = e.target.files?.[0];
										if (file) void handleFile(file);
										e.target.value = '';
									}}
								/>

								<div className="flex flex-col gap-4 px-6 pb-5">
									<p
										data-testid="import-contacts-instructions"
										className="text-sm text-(--text-muted)"
									>
										Upload a CSV or Excel (.xlsx) file with a{' '}
										<span className="font-medium text-(--text-primary)">
											name
										</span>{' '}
										column plus at least one of{' '}
										<span className="font-medium text-(--text-primary)">
											email
										</span>
										,{' '}
										<span className="font-medium text-(--text-primary)">
											phone
										</span>
										, or{' '}
										<span className="font-medium text-(--text-primary)">
											instagram
										</span>
										.
									</p>

									{hasFile && !error ? (
										<div className="flex flex-col gap-3">
											<div
												className="flex items-center gap-3 rounded-xl border
													border-(--glass-border) bg-(--color-beacon-ivory) px-4
													py-3"
											>
												<div
													className="flex h-9 w-9 shrink-0 items-center
														justify-center rounded-lg bg-white
														text-(--text-muted) ring-1 ring-(--glass-border)"
												>
													<FileIcon />
												</div>
												<div className="flex min-w-0 flex-col">
													<p
														className="truncate text-sm font-medium
															text-(--text-primary)"
													>
														{fileName}
													</p>
													<p
														data-testid="import-contacts-summary"
														className="text-xs text-green-600"
													>
														{rows.length} valid contact
														{rows.length === 1 ? '' : 's'} found
														{skippedCount > 0 && (
															<span
																data-testid="import-contacts-skipped"
																className="text-(--text-muted)"
															>
																{' '}
																· {skippedCount} row
																{skippedCount === 1 ? '' : 's'} skipped
															</span>
														)}
													</p>
												</div>
											</div>
											<button
												type="button"
												disabled={isImporting}
												onClick={() => fileInputRef.current?.click()}
												className="self-start text-sm font-medium
													text-(--color-beacon-accent) transition-colors
													hover:text-(--color-beacon-accent-hover)
													disabled:opacity-50"
											>
												Choose a different file
											</button>
										</div>
									) : (
										<DropZone
											onDrop={handleDrop}
											isFilled={false}
											replaceMessage="Drop CSV to replace"
										>
											<div
												data-testid="import-contacts-choose-file"
												className="flex w-full cursor-pointer flex-col
													items-center justify-center px-6 py-8"
												onClick={() => fileInputRef.current?.click()}
											>
												<IllustratedMessage>
													<CloudUpload />
													<Heading>Choose a file</Heading>
													<Content>CSV or Excel (.xlsx) up to 5MB</Content>
												</IllustratedMessage>
											</div>
										</DropZone>
									)}

									{notice && !error && (
										<p
											data-testid="import-contacts-notice"
											className="rounded-lg border border-amber-200 bg-amber-50
												px-3 py-2 text-sm text-amber-900"
										>
											{notice}
										</p>
									)}

									{error && (
										<p
											data-testid="import-contacts-error"
											className="text-sm text-(--color-beacon-error)"
										>
											{error}
										</p>
									)}
								</div>

								<div className="flex justify-end px-6 pt-1 pb-5">
									<ButtonGroup align="end">
										<ActionButton
											isQuiet
											isDisabled={isImporting}
											onPress={close}
										>
											<Text>Cancel</Text>
										</ActionButton>
										<Button
											variant="accent"
											data-testid="import-contacts-submit"
											isPending={isImporting}
											isDisabled={!hasFile || isImporting}
											onPress={() => void handleImport()}
										>
											<Text>
												Import {rows.length} contact
												{rows.length === 1 ? '' : 's'}
											</Text>
										</Button>
									</ButtonGroup>
								</div>
							</>
						)}
					</div>
				</div>
			)}
		</>
	);
}
